import React from "react";
import {
  IonAvatar,
  IonCol,
  IonIcon,
  IonItem,
  IonLabel,
  IonRow,
  IonButton,
} from "@ionic/react";
import { checkmarkCircle, personCircle } from "ionicons/icons";
import { chevronForwardOutline as rightArrowIcon } from "ionicons/icons";
import clsx from "clsx";

import {
  IOnClickStudentAvatarParams,
  IRealization,
  IFeedback,
  RealizationStamp,
} from "../../../state";
import ImageDetailModal from "../../ImageDetailModal";
import RealizationContent from "../../RealizationContent";
import StampSelector from "../../StampSelector";
import ModalConfirm from "../../ModalConfirm";
import { loadStamp } from "../../../libs/stamp";
import { useSubmitOnCtrlEnter } from "../../../hooks/useSubmitOnCtrlEnter";
import editIcon from "../../../assets/icons/edit.svg";
import trashIcon from "../../../assets/icons/trash.svg";

import styles from "./RealizationItem.module.scss";

export interface RealizationItemProps {
  realization: IRealization;
  hideReactions?: boolean;
  hideCheckedStatus?: boolean;
  feedbacks?: IFeedback[];
  currentTeacherId?: number;
  onClick?: () => void;
  onAvatarClick?: (params: IOnClickStudentAvatarParams) => void;
  onFeedbackSubmit?: (realizationId: number, content: string) => void;
  onStampSubmit?: (realizationId: number, stamp: RealizationStamp) => void;
  onDeleteFeedback?: (feedbackId: number) => void;
  onUpdateFeedback?: (feedbackId: number, content: string) => void;
  addFavorite?: (id: number) => void;
  removeFavorite?: (id: number) => void;
}

const RealizationItem: React.FC<RealizationItemProps> = ({
  realization,
  hideReactions,
  hideCheckedStatus,
  feedbacks = [],
  currentTeacherId,
  onClick,
  onAvatarClick,
  onFeedbackSubmit,
  onStampSubmit,
  onDeleteFeedback,
  onUpdateFeedback,
  addFavorite,
  removeFavorite,
}) => {
  const [state, setState] = React.useState({
    showImageDetail: false,
    feedbackText: "",
    editTargetID: 0,
    showDeleteModal: false,
    deleteFeedbackId: null as number | null,
  });
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const feedbackScrollRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const element = feedbackScrollRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  };

  React.useEffect(() => {
    if (feedbacks.length > 0) {
      scrollToBottom();
    }
  }, [feedbacks.length]);

  const handleClickAvatar = React.useCallback(
    e => {
      if (!onAvatarClick || !realization.student) return;
      e.stopPropagation();

      const { id, full_name, photo, klass_id, klass_name } =
        realization.student;
      onAvatarClick({
        student_id: id,
        name: full_name,
        photo,
        class_id: klass_id,
        class_name: klass_name,
      });
    },
    [onAvatarClick, realization.student],
  );

  const submitFeedback = React.useCallback(() => {
    if (!state.feedbackText.trim()) return;

    if (state.editTargetID > 0 && onUpdateFeedback) {
      onUpdateFeedback(state.editTargetID, state.feedbackText.trim());
    } else if (onFeedbackSubmit) {
      onFeedbackSubmit(realization.id, state.feedbackText.trim());
    }
    setState(s => ({
      ...s,
      feedbackText: "",
      editTargetID: 0,
    }));
    scrollToBottom();
  }, [
    onFeedbackSubmit,
    onUpdateFeedback,
    realization.id,
    state.feedbackText,
    state.editTargetID,
  ]);

  const handleFeedbackSubmit = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      submitFeedback();
    },
    [submitFeedback],
  );

  const handleTextareaKeyDown = useSubmitOnCtrlEnter(submitFeedback);

  const handleTextareaChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      setState(s => ({ ...s, feedbackText: e.target.value }));
    },
    [],
  );

  const handleStampSelect = React.useCallback(
    (stamp: RealizationStamp) => {
      if (onStampSubmit) {
        onStampSubmit(realization.id, stamp);
        scrollToBottom();
      }
    },
    [onStampSubmit, realization.id],
  );

  const handleEditFeedback = React.useCallback(
    (feedbackId: number, content: string) => {
      setState(s => ({
        ...s,
        editTargetID: feedbackId,
        feedbackText: content,
      }));
      textRef.current?.focus();
    },
    [],
  );

  const handleDeleteConfirm = React.useCallback(() => {
    if (state.deleteFeedbackId && onDeleteFeedback) {
      onDeleteFeedback(state.deleteFeedbackId);
      setState(s => ({ ...s, showDeleteModal: false, deleteFeedbackId: null }));
    }
  }, [state.deleteFeedbackId, onDeleteFeedback]);

  const handleFavoriteFunc = React.useCallback(() => {
    if (realization.is_favorite) {
      removeFavorite?.(realization.id);
    } else {
      addFavorite?.(realization.id);
    }
  }, [addFavorite, removeFavorite, realization]);

  return (
    <>
      {realization.image && (
        <ImageDetailModal
          show={state.showImageDetail}
          imageUrl={realization.image.url}
          imageName={realization.image.name}
          onClose={() => setState(s => ({ ...s, showImageDetail: false }))}
        />
      )}
      <IonItem
        lines="none"
        className={styles.realizationItem}
        onClick={onClick}
        button
        detail={false}
      >
        <div
          className={clsx(
            styles.avatarArea,
            !!realization.student && !!onAvatarClick && styles.clicableAvatar,
          )}
          onClick={handleClickAvatar}
        >
          {realization.student?.photo?.url ? (
            <IonAvatar slot="start" color="light" className={styles.avatar}>
              <img alt="プロフィール画像" src={realization.student.photo.url} />
            </IonAvatar>
          ) : (
            <IonIcon
              className={styles.avatar}
              icon={personCircle}
              slot="start"
              color="light"
            />
          )}
        </div>
        <IonRow className={styles.row}>
          <IonCol size="12" className={styles.wrapperContent}>
            <div className={styles.container}>
              <div className={styles.topWrapper}>
                <div className={styles.nameArea}>
                  <IonLabel color="dark">
                    <span className={styles.name}>
                      {realization.is_other_school
                        ? "他校の生徒"
                        : realization.student?.full_name ?? ""}
                    </span>
                  </IonLabel>
                  {realization.is_checked && !hideCheckedStatus && (
                    <div className={styles.checkedStatus}>
                      <IonIcon
                        icon={checkmarkCircle}
                        className={styles.checkIcon}
                      />
                      <span>先生確認済</span>
                    </div>
                  )}
                </div>
              </div>
              <RealizationContent
                realization={realization}
                hideReactions={hideReactions}
                color={"light"}
                onClickThumbnailImage={() => {
                  setState(s => ({ ...s, showImageDetail: true }));
                }}
                onClickFavorite={handleFavoriteFunc}
              />
            </div>
            <IonIcon
              color="medium"
              icon={rightArrowIcon}
              className={styles.iconCaretRight}
            />
          </IonCol>
        </IonRow>
      </IonItem>
      {onFeedbackSubmit && (
        <div className={styles.feedbackSection}>
          <div className={styles.feedbackContainer}>
            {feedbacks.length > 0 && (
              <div
                className={styles.feedbackScrollWrapper}
                ref={feedbackScrollRef}
              >
                {feedbacks.map(feedback => {
                  const stamp = loadStamp(
                    feedback.realization_stamp_reaction?.id,
                  );
                  return (
                    <div key={feedback.id} className={styles.feedbackItem}>
                      <div className={styles.feedbackContent}>
                        {stamp ? (
                          <img
                            src={stamp.image}
                            alt={stamp.name}
                            className={styles.stampImage}
                          />
                        ) : (
                          feedback.content
                        )}
                      </div>
                      <div className={styles.feedbackMeta}>
                        {feedback.teacher_id === currentTeacherId && (
                          <div className={styles.feedbackActions}>
                            {!stamp && (
                              <IonIcon
                                icon={editIcon}
                                className={styles.actionIcon}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEditFeedback(
                                    feedback.id,
                                    feedback.content,
                                  );
                                }}
                              />
                            )}
                            <IonIcon
                              icon={trashIcon}
                              className={styles.actionIcon}
                              onClick={e => {
                                e.stopPropagation();
                                setState(s => ({
                                  ...s,
                                  showDeleteModal: true,
                                  deleteFeedbackId: feedback.id,
                                }));
                              }}
                            />
                          </div>
                        )}
                        <span className={styles.feedbackTime}>
                          {new Date(feedback.created_at)
                            .toLocaleDateString("ja-JP", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                            .replace(/\//g, "/")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div
              className={styles.inputArea}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.inputWrapper}>
                <textarea
                  placeholder="フィードバックする"
                  ref={textRef}
                  className={styles.input}
                  value={state.feedbackText}
                  onChange={handleTextareaChange}
                  onKeyDown={handleTextareaKeyDown}
                  rows={1}
                />
                {state.editTargetID > 0 && (
                  <span
                    className={styles.cancelButton}
                    onClick={e => {
                      e.stopPropagation();
                      setState(s => ({
                        ...s,
                        feedbackText: "",
                        editTargetID: 0,
                      }));
                    }}
                  >
                    キャンセル
                  </span>
                )}
                {state.editTargetID === 0 && onStampSubmit && (
                  <div className={styles.stampSelectorWrapper}>
                    <StampSelector
                      layout="categorized"
                      onSelect={handleStampSelect}
                    />
                  </div>
                )}
                <IonButton
                  className={styles.submitButton}
                  disabled={!state.feedbackText.trim()}
                  onClick={handleFeedbackSubmit}
                >
                  {state.editTargetID > 0 ? "更新" : "投稿"}
                </IonButton>
              </div>
            </div>
          </div>
        </div>
      )}
      <ModalConfirm
        isOpen={state.showDeleteModal}
        loading={false}
        whiteMode
        message={`フィードバックを削除しますか？\nこの操作は取り消しができません。`}
        buttons={[
          {
            title: "キャンセル",
            action: () =>
              setState(s => ({
                ...s,
                showDeleteModal: false,
                deleteFeedbackId: null,
              })),
            type: "None",
          },
          {
            title: "削除する",
            action: handleDeleteConfirm,
            type: "Danger",
          },
        ]}
      />
    </>
  );
};

export default RealizationItem;
